import { Modal } from 'antd';
import { Stack } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import InvitationsList from 'components/InvitationsList';
import MembersList from 'components/MembersList';
import UsersList from 'components/UsersList';

import { TeamMemberRole } from 'interfaces/teams';
import { User } from 'interfaces/users';

import { fetchUsers as fetchCategoryUsers } from 'slices/categoriesActions';
import { fetchUsers as fetchDocumentUsers } from 'slices/documentsActions';
import { fetchInvitations, syncInvitations } from 'slices/invitationsActions';
import { fetchGuestTeamMembers } from 'slices/teamsActions';
import { reset as resetTeam } from 'slices/teamSlice';

import { isCategory } from '../../utilities/constants';
import { useAppDispatch, useAppSelector } from 'utilities/hooks';
import { Category, Document } from 'interfaces/documents';
import { Invitation } from 'interfaces/invitations';
import { SelectSharingDocumentTabs, SharedTabType } from './SelectSharingDocumentTabs';
import { closeModal } from 'slices/modalsSlice';
import { ActionSharingMembers } from './ActionSharingMembers';
import { ActionSharingByLink } from './ActionSharingByLink';
import { setterShareable } from '../../slices/shareablesSlice';

export interface IGuestTeamModalProps {
    entity?: Category | Document;
}

export interface IGuestTeamModalCallbacks {
    onSubmit?: () => void;
    onClose?: () => void;
}

export const GuestTeamModal = ({
    id,
    zIndex,
    props,
    callbacks,
}: {
    id: string;
    zIndex: number;
    props: IGuestTeamModalProps;
    callbacks: IGuestTeamModalCallbacks;
}) => {
    const { entity } = props;
    const { onSubmit, onClose } = callbacks;
    const entityId = isCategory(entity) ? (entity as Category)?.uuid : (entity as Document)?.id;

    const dispatch = useAppDispatch();

    const [role, setRole] = useState(TeamMemberRole.VIEWER);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [currentTab, setCurrentTab] = useState<SharedTabType>('members');
    const [entityUsersLoading, setEntityUsersLoading] = useState<boolean>(false);
    const [entityUsers, setEntityUsers] = useState<User[]>([]);
    const [isLoaded, setIsLoaded] = useState<boolean>(false);
    const [hasExistingTeam, setHasExistingTeam] = useState<boolean>(false);

    const {
        loading: teamLoading,
        members: teamMembers,
    } = useAppSelector((state) => state.team);
    const { invitations } = useAppSelector((state) => state.invitations);

    const invitationsRef = useRef<Invitation[]>(invitations || []);

    useEffect(() => {
        if (id) {
            setIsOpen(true);
        }
    }, [id]);

    useEffect(() => {
        setIsLoaded(!(teamLoading || entityUsersLoading));
        if (!(teamLoading || entityUsersLoading)) {
            const existingMembers = [...teamMembers, ...entityUsers].map((member: User) => member.id);
            dispatch(setterShareable({ excludedMembers: Array.from(new Set(existingMembers)) }));
        }
    }, [teamLoading, entityUsersLoading]);

    useEffect(() => {
        if (!isOpen) return;

        resetDefaults();

        if (entity) {
            const fetchEntityUsersAction = isCategory(entity) ? fetchCategoryUsers : fetchDocumentUsers;

            setEntityUsersLoading(true);
            dispatch(fetchEntityUsersAction(entityId))
                .then((_) => setEntityUsers([]))
                .finally(() => setEntityUsersLoading(false));

            setHasExistingTeam(false);
            dispatch(fetchGuestTeamMembers(entityId))
                .unwrap()
                .then((res: any) => setHasExistingTeam((res?.data?.length ?? 0) > 0))
                .catch(() => {});
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        if (invitationsRef.current.length > (invitations || []).length) {
            let data: any = {
                teamId: entityId,
                invitations: (invitations || []).map((invitation: Invitation) => {
                    return {
                        id: invitation.id,
                        email: invitation.email,
                        phone: invitation.phone,
                        team_role: role,
                    };
                }),
            };
            dispatch(syncInvitations(data));
        }
        invitationsRef.current = invitations || [];
    }, [invitations]);

    const close = () => {
        resetDefaults();
        dispatch(closeModal({ modal: 'guestTeamModal', id }));
    };

    const resetDefaults = () => {
        setRole(TeamMemberRole.VIEWER);
        dispatch(
            setterShareable({
                memberIdListSelected: [],
                includeAnnotations: false,
                shareRecipientsError: '',
                shareRecipients: [{}],
            }),
        );
    };

    const handleOnClose = () => {
        dispatch(resetTeam());
        onClose?.();
        close();
    };

    const handleOnSubmit = () => {
        onSubmit?.();
        close();
    };

    return (
        <>
            <Modal
                zIndex={zIndex}
                title={'Manage Access and Sharing'}
                open={isOpen}
                onCancel={handleOnClose}
                footer={
                    currentTab === 'members' ? (
                        <ActionSharingMembers
                            close={close}
                            onSubmit={handleOnSubmit}
                            role={role}
                            entity={entity}
                            hasExistingTeam={hasExistingTeam}
                        />
                    ) : (
                        <ActionSharingByLink close={close} onSubmit={handleOnSubmit} entity={entity} />
                    )
                }
                width={800}
            >
                {isOpen ? (
                    <Stack spacing={2}>
                        <SelectSharingDocumentTabs
                            isLoaded={isLoaded}
                            setCurrentTab={setCurrentTab}
                            role={role}
                            setRole={setRole}
                        />

                        {currentTab === 'members' && (
                            <>
                                {teamMembers ? <MembersList /> : <></>}
                                {invitations ? (
                                    <InvitationsList invitations={invitations} onResend={() => {}}></InvitationsList>
                                ) : (
                                    <></>
                                )}
                                {entityUsers ? <UsersList users={entityUsers}></UsersList> : <></>}
                            </>
                        )}
                    </Stack>
                ) : (
                    <></>
                )}
            </Modal>
        </>
    );
};
